import React, { useState, useEffect } from 'react';
import { XMarkIcon, PencilIcon, CheckIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { RestaurantProfile, Theme } from '../types';
import { apiService } from '../services/api';
import PricingPlansModal from './PricingPlansModal';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';

const Settings: React.FC = () => {
  const { restaurantDetails, loadRestaurantDetails } = useAuth();
  const { showSuccess, showError } = useToast();
  const [profile, setProfile] = useState<RestaurantProfile>({
    name: '',
    id: '',
    address: '',
    description: '',
    email: '',
    hours: {
      monday: '',
      tuesday: '',
      wednesday: '',
      thursday: '',
      friday: '',
      saturday: '',
      sunday: ''
    },
    logo: '',
    coverImage: '',
    socialMedia: {
      facebook: '',
      instagram: '',
      x: ''
    },
    mapurl: '',
    plan: undefined,
    enable_table_ordering: false,
    enable_whatsapp_ordering: false,
    auto_remove_user: false,
    remove_user_time_in_hour: 0,
    remove_user_time_in_minute: 0,
    enable_left_table: false,
    enable_notification: false,
    enable_help: false,
    hide_price: false,
    address_enabled: false,
    payment_option: 'disabled',
    razorpay_key_id: '',
    razorpay_key_secret: '',
    upi_id: '',
    is_prepaid: false,
    stop_auto_payment_link: false
  });

  const [loading, setLoading] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [updatingToggle, setUpdatingToggle] = useState<string | null>(null);
  const [showRazorpayConfigFields, setShowRazorpayConfigFields] = useState(true);
  const [showThemeCustomizer, setShowThemeCustomizer] = useState(false);
  const [customTheme, setCustomTheme] = useState<Theme>({
    id: 'custom',
    name: 'Custom Theme',
    primary: '#f97316',
    secondary: '#db2777',
    accent: '#ea580c',
    background: '#fff7ed',
    surface: '#ffffff',
    text: '#111827',
    textSecondary: '#4b5563'
  });

  const predefinedThemes: Theme[] = [
    {
      id: 'menunix-brand',
      name: 'Menunix Gradient',
      primary: '#f97316',
      secondary: '#db2777',
      accent: '#ea580c',
      background: '#fff7ed',
      surface: '#ffffff',
      text: '#111827',
      textSecondary: '#4b5563'
    },
    {
      id: 'orange-green',
      name: 'Orange & Green',
      primary: '#FF6B35',
      secondary: '#4CAF50',
      accent: '#F7931E',
      background: '#f8f9fa',
      surface: '#ffffff',
      text: '#212121',
      textSecondary: '#757575'
    },
    {
      id: 'blue-purple',
      name: 'Blue & Purple',
      primary: '#2196F3',
      secondary: '#9C27B0',
      accent: '#03A9F4',
      background: '#f5f7fa',
      surface: '#ffffff',
      text: '#212121',
      textSecondary: '#757575'
    },
    {
      id: 'red-pink',
      name: 'Red & Pink',
      primary: '#F44336',
      secondary: '#E91E63',
      accent: '#FF5722',
      background: '#fdf2f2',
      surface: '#ffffff',
      text: '#212121',
      textSecondary: '#757575'
    },
    {
      id: 'teal-cyan',
      name: 'Teal & Cyan',
      primary: '#009688',
      secondary: '#00BCD4',
      accent: '#4DB6AC',
      background: '#f0fdfc',
      surface: '#ffffff',
      text: '#212121',
      textSecondary: '#757575'
    },
    {
      id: 'indigo-blue',
      name: 'Indigo & Blue',
      primary: '#3F51B5',
      secondary: '#2196F3',
      accent: '#5C6BC0',
      background: '#f3f4f6',
      surface: '#ffffff',
      text: '#212121',
      textSecondary: '#757575'
    },
    {
      id: 'amber-orange',
      name: 'Amber & Orange',
      primary: '#FFC107',
      secondary: '#FF9800',
      accent: '#FFB74D',
      background: '#fffbf0',
      surface: '#ffffff',
      text: '#212121',
      textSecondary: '#757575'
    },
    {
      id: 'dark-theme',
      name: 'Dark Theme',
      primary: '#BB86FC',
      secondary: '#03DAC6',
      accent: '#CF6679',
      background: '#121212',
      surface: '#1E1E1E',
      text: '#FFFFFF',
      textSecondary: '#B3B3B3'
    }
  ];

  // Ensure page starts at top when visiting this screen
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  useEffect(() => {
    loadProfile();
  }, [restaurantDetails]);


  const loadProfile = async () => {
    setLoading(true);
    try {
      // First try to load from localStorage (via restaurantDetails from context)
      let profileData: RestaurantProfile | null = null;
      
      if (restaurantDetails) {
        profileData = {
          ...restaurantDetails,
          hours: restaurantDetails.hours || {
            monday: '',
            tuesday: '',
            wednesday: '',
            thursday: '',
            friday: '',
            saturday: '',
            sunday: ''
          },
          socialMedia: restaurantDetails.socialMedia || {
            facebook: '',
            instagram: '',
            x: ''
          },
          theme: restaurantDetails.theme || predefinedThemes[0] // Default to Menunix brand
        };
      } else {
        // Fallback to localStorage directly if context doesn't have it
        const storedDetails = localStorage.getItem('restaurantDetails');
        if (storedDetails) {
          try {
            const parsed = JSON.parse(storedDetails);
            profileData = {
              ...parsed,
              hours: parsed.hours || {
                monday: '',
                tuesday: '',
                wednesday: '',
                thursday: '',
                friday: '',
                saturday: '',
                sunday: ''
              },
              socialMedia: parsed.socialMedia || {
                facebook: '',
                instagram: '',
                x: ''
              },
              theme: parsed.theme || predefinedThemes[0]
            };
          } catch (error) {
            console.error('Error parsing restaurantDetails from localStorage:', error);
          }
        }
      }

      if (profileData) {
        setProfile(profileData);
        setShowRazorpayConfigFields(!Boolean((profileData as any).razorpay_config_updated));
        // Set custom theme if current theme is custom
        if (profileData.theme?.id === 'custom') {
          setCustomTheme(profileData.theme);
        }
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  };


  const handleToggleChange = async (field: 'enable_whatsapp_ordering' | 'enable_table_ordering' | 'hide_price' | 'address_enabled' | 'is_prepaid' | 'stop_auto_payment_link', value: boolean) => {
    setUpdatingToggle(field);
    try {
      const updatedProfile = { ...profile, [field]: value };
      
      // Make enable_whatsapp_ordering and hide_price mutually exclusive
      if (field === 'enable_whatsapp_ordering' && value === true) {
        updatedProfile.hide_price = false;
      } else if (field === 'hide_price' && value === true) {
        updatedProfile.enable_whatsapp_ordering = false;
      }
      
      setProfile(updatedProfile);
      
      const response = await apiService.updateRestaurantProfile(updatedProfile);
      if (response.status) {
        // Update localStorage after successful API call
        localStorage.setItem('restaurantDetails', JSON.stringify(updatedProfile));
        // Refresh AuthContext to sync the state
        await loadRestaurantDetails(false);
        const featureNames: { [key: string]: string } = {
          enable_whatsapp_ordering: 'WhatsApp ordering',
          enable_table_ordering: 'Table ordering',
          hide_price: 'WhatsApp Enquiry Mode',
          address_enabled: 'Customer Address',
          is_prepaid: 'Prepayment requirement',
          stop_auto_payment_link: 'Auto payment link sending'
        };
        const featureName = featureNames[field] || 'Setting';
        showSuccess('Success', response.message || `${featureName} ${value ? 'enabled' : 'disabled'} successfully!`);
      } else {
        setProfile(prev => ({ ...prev, [field]: !value }));
        const featureNames: { [key: string]: string } = {
          enable_whatsapp_ordering: 'WhatsApp ordering',
          enable_table_ordering: 'Table ordering',
          hide_price: 'WhatsApp Enquiry Mode',
          address_enabled: 'Customer Address',
          is_prepaid: 'Prepayment requirement',
          stop_auto_payment_link: 'Auto payment link sending'
        };
        const featureName = featureNames[field] || 'Setting';
        showError('Error', response.message || `Failed to update ${featureName} setting`);
      }
    } catch (error) {
      console.error('Error updating toggle:', error);
      const message = (error as Error)?.message || 'Failed to update setting. Please try again.';
      showError('Error', message);
    } finally {
      setUpdatingToggle(null);
    }
  };

  const handlePaymentOptionChange = async (option: 'disabled' | 'razorpay' | 'upi') => {
    setUpdatingToggle('payment_option');
    try {
      const shouldShowRazorpayFields = option === 'razorpay' && !Boolean(profile.razorpay_config_updated);
      setShowRazorpayConfigFields(shouldShowRazorpayFields);
      const updatedProfile = {
        ...profile,
        payment_option: option,
        razorpay_key_id: option === 'razorpay' ? (profile.razorpay_key_id || '') : '',
        razorpay_key_secret: option === 'razorpay' ? (profile.razorpay_key_secret || '') : '',
        upi_id: profile.upi_id || ''
      };
      setProfile(updatedProfile);

      const response = await apiService.updateRestaurantProfile(updatedProfile);
      if (response.status) {
        localStorage.setItem('restaurantDetails', JSON.stringify(updatedProfile));
        await loadRestaurantDetails(false);
        showSuccess('Success', response.message || 'Payment option updated successfully!');
      } else {
        showError('Error', response.message || 'Failed to update payment option');
      }
    } catch (error) {
      console.error('Error updating payment option:', error);
      const message = (error as Error)?.message || 'Failed to update payment option. Please try again.';
      showError('Error', message);
    } finally {
      setUpdatingToggle(null);
    }
  };

  const handlePaymentFieldBlur = async (field: 'razorpay_key_id' | 'razorpay_key_secret' | 'upi_id', value: string) => {
    setUpdatingToggle(field);
    try {
      const trimmedValue = value.trim();
      const updatedProfile = { ...profile, [field]: trimmedValue };
      setProfile(updatedProfile);

      // For Razorpay, save only when both credentials are present.
      if (field === 'razorpay_key_id' || field === 'razorpay_key_secret') {
        const nextKeyId = field === 'razorpay_key_id' ? trimmedValue : (updatedProfile.razorpay_key_id || '').trim();
        const nextKeySecret = field === 'razorpay_key_secret' ? trimmedValue : (updatedProfile.razorpay_key_secret || '').trim();
        if (!nextKeyId || !nextKeySecret) {
          return;
        }
      }

      const response = await apiService.updateRestaurantProfile(updatedProfile);
      if (response.status) {
        localStorage.setItem('restaurantDetails', JSON.stringify(updatedProfile));
        await loadRestaurantDetails(false);
        showSuccess('Success', response.message || 'Payment credential updated successfully!');
      } else {
        showError('Error', response.message || 'Failed to update payment credential');
      }
    } catch (error) {
      console.error('Error updating payment credential:', error);
      const message = (error as Error)?.message || 'Failed to update payment credential. Please try again.';
      showError('Error', message);
    } finally {
      setUpdatingToggle(null);
    }
  };

  const handleThemeSelect = async (theme: Theme) => {
    setUpdatingToggle('theme');
    try {
      const updatedProfile = { ...profile, theme };
      setProfile(updatedProfile);
      
      const response = await apiService.updateRestaurantProfile(updatedProfile);
      if (response.status) {
        // Update localStorage after successful API call
        localStorage.setItem('restaurantDetails', JSON.stringify(updatedProfile));
        // Refresh AuthContext to sync the state
        await loadRestaurantDetails(false);
        showSuccess('Success', response.message || `Theme "${theme.name}" applied successfully!`);
      } else {
        showError('Error', response.message || 'Failed to update theme');
      }
    } catch (error) {
      console.error('Error updating theme:', error);
      const message = (error as Error)?.message || 'Failed to update theme. Please try again.';
      showError('Error', message);
      // Revert to previous theme
      const previousTheme = profile.theme || predefinedThemes[1];
      setProfile({ ...profile, theme: previousTheme });
    } finally {
      setUpdatingToggle(null);
    }
  };

  const handleCustomThemeSave = async () => {
    setUpdatingToggle('theme');
    try {
      const updatedProfile = { ...profile, theme: customTheme };
      setProfile(updatedProfile);
      
      const response = await apiService.updateRestaurantProfile(updatedProfile);
      if (response.status) {
        // Update localStorage after successful API call
        localStorage.setItem('restaurantDetails', JSON.stringify(updatedProfile));
        // Refresh AuthContext to sync the state
        await loadRestaurantDetails(false);
        showSuccess('Success', response.message || 'Custom theme saved successfully!');
        setShowThemeCustomizer(false);
      } else {
        showError('Error', response.message || 'Failed to save custom theme');
      }
    } catch (error) {
      console.error('Error saving custom theme:', error);
      const message = (error as Error)?.message || 'Failed to save custom theme. Please try again.';
      showError('Error', message);
    } finally {
      setUpdatingToggle(null);
    }
  };

  const handleResetTheme = () => {
    setCustomTheme({
      id: 'custom',
      name: 'Custom Theme',
      primary: '#2196F3',
      secondary: '#9C27B0',
      accent: '#03A9F4',
      background: '#f5f7fa',
      surface: '#ffffff',
      text: '#212121',
      textSecondary: '#757575'
    });
    showSuccess('Success', 'Theme values reset to default!');
  };

  const getCurrentTheme = (): Theme => {
    return profile.theme || predefinedThemes[0]; // Default to Menunix brand
  };

  // Check if user has any plan (basic or pro)
  const hasAnyPlan = profile.plan === 'pro' || profile.plan === 'basic' || restaurantDetails?.plan === 'pro' || restaurantDetails?.plan === 'basic';
  
  // Check if user has pro plan
  const hasProPlan = profile.plan === 'pro' || restaurantDetails?.plan === 'pro';

  // Check if user is in trial mode
  const isTrialMode = Boolean((restaurantDetails as any)?.trial_mode) || Boolean((profile as any)?.trial_mode);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  // Show access denied if no plan and not in trial mode
  if (!hasAnyPlan && !isTrialMode) {
    return (
      <div className="space-y-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <div className="flex items-center">
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-yellow-900 mb-2">
                Plan Required
              </h3>
              <p className="text-yellow-800">
                Settings page is only available for Basic and Pro plan users. Please select a plan to access these features.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Subscription Plan */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="space-y-6">
          <h2 className="text-xl font-semibold text-gray-900 border-b pb-2">Subscription Plan</h2>
          
          <div className="bg-gray-50 rounded-lg p-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="space-y-2 sm:space-y-3">
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Status: {isTrialMode ? (
                    <span className="px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                      Free Trial (15 Days)
                    </span>
                  ) : profile.plan ? (
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      profile.plan === 'pro' 
                        ? 'bg-orange-100 text-orange-800' 
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {profile.plan === 'pro' ? 'Pro Plan' : 'Active'}
                    </span>
                  ) : (
                    <span className="text-gray-500">No plan selected</span>
                  )}
                </h3>
                <p className="text-gray-600">
                  {isTrialMode
                    ? 'You are currently on a 15-day free trial. Upgrade to continue using all features after the trial period ends.'
                    : profile.plan === 'pro' 
                    ? 'Full online ordering with table management and analytics'
                    : profile.plan === 'basic'
                    ? 'Digital catalog with WhatsApp ordering'
                    : 'Choose a plan to unlock features for your business'
                  }
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowPricingModal(true)}
                className="w-full sm:w-auto px-6 py-3 bg-orange-600 text-white font-medium rounded-lg hover:bg-orange-700 focus:ring-2 focus:ring-pink-600 focus:ring-offset-2 transition-colors"
              >
                {isTrialMode 
                  ? 'Upgrade Now' 
                  : profile.plan 
                  ? 'Cancel Subscription' 
                  : 'Select Plan'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Ordering Options */}
      {hasAnyPlan && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-gray-900 border-b pb-2">Ordering Options</h2>
            
            <div className="space-y-6">
              {/* Online Ordering */}
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1 pr-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      Online Ordering
                    </h3>
                    <p className="text-gray-600">
                      Allow customers to place orders from your website.
                    </p>
                  </div>
                  <label className={`relative inline-flex items-center ${updatingToggle !== 'enable_whatsapp_ordering' ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}>
                    <input
                      type="checkbox"
                      checked={profile.enable_whatsapp_ordering || false}
                      onChange={(e) => handleToggleChange('enable_whatsapp_ordering', e.target.checked)}
                      disabled={updatingToggle === 'enable_whatsapp_ordering'}
                      className="sr-only peer"
                    />
                    <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-pink-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600 ${updatingToggle === 'enable_whatsapp_ordering' ? 'opacity-50' : ''}`}>
                      {updatingToggle === 'enable_whatsapp_ordering' && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        </div>
                      )}
                    </div>
                  </label>
                </div>
              </div>

              {/* Customer Address (sub option of Online Ordering) */}
              {profile.enable_whatsapp_ordering && (
                <div className="bg-orange-50 border border-orange-100 rounded-lg p-6 ml-0 md:ml-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 pr-6">
                      <h3 className="text-lg font-medium text-gray-900 mb-2">
                        Customer Address
                      </h3>
                      <p className="text-gray-600">
                        Collect customer address details during online checkout.
                      </p>
                    </div>
                    <label className={`relative inline-flex items-center ${updatingToggle !== 'address_enabled' ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}>
                      <input
                        type="checkbox"
                        checked={profile.address_enabled || false}
                        onChange={(e) => handleToggleChange('address_enabled', e.target.checked)}
                        disabled={updatingToggle === 'address_enabled'}
                        className="sr-only peer"
                      />
                      <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-pink-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600 ${updatingToggle === 'address_enabled' ? 'opacity-50' : ''}`}>
                        {updatingToggle === 'address_enabled' && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          </div>
                        )}
                      </div>
                    </label>
                  </div>
                </div>
              )}

              {/* Table Ordering */}
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1 pr-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      Table Ordering
                    </h3>
                    <p className="text-gray-600">
                      Enable QR-based dine-in ordering so guests can scan and order directly from their table.
                    </p>
                  </div>
                  <label className={`relative inline-flex items-center ${updatingToggle !== 'enable_table_ordering' ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}>
                    <input
                      type="checkbox"
                      checked={profile.enable_table_ordering || false}
                      onChange={(e) => handleToggleChange('enable_table_ordering', e.target.checked)}
                      disabled={updatingToggle === 'enable_table_ordering'}
                      className="sr-only peer"
                    />
                    <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-pink-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600 ${updatingToggle === 'enable_table_ordering' ? 'opacity-50' : ''}`}>
                      {updatingToggle === 'enable_table_ordering' && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        </div>
                      )}
                    </div>
                  </label>
                </div>
              </div>

            </div>
          </div>
        </div>
      )}

      {/* Payment Options */}
      {hasAnyPlan && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-gray-900 border-b pb-2">Payment Options</h2>

            <div className="bg-gray-50 rounded-lg p-6 space-y-5">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Preferred Payment Mode</h3>
                <p className="text-gray-600">
                  Choose how customers can complete online payments for their orders.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {[
                  { id: 'razorpay', label: 'Razorpay' },
                  { id: 'upi', label: 'UPI' },
                  { id: 'disabled', label: 'Disable Payment' }
                ].map((option) => (
                  <label
                    key={option.id}
                    className={`flex items-center gap-3 border rounded-lg px-4 py-3 bg-white transition-colors ${
                      profile.payment_option === option.id ? 'border-orange-500 ring-1 ring-orange-200' : 'border-gray-200'
                    } ${updatingToggle === 'payment_option' ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:border-gray-300'}`}
                  >
                    <input
                      type="radio"
                      name="payment_option"
                      value={option.id}
                      checked={profile.payment_option === option.id}
                      onChange={() => handlePaymentOptionChange(option.id as 'disabled' | 'razorpay' | 'upi')}
                      disabled={updatingToggle === 'payment_option'}
                      className="text-orange-600 focus:ring-pink-200"
                    />
                    <span className="text-sm font-medium text-gray-800">{option.label}</span>
                  </label>
                ))}
              </div>

              {profile.payment_option === 'razorpay' && (
                <>
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-orange-900 mb-1">New to Razorpay?</h4>
                    <p className="text-sm text-orange-800">
                      Create a Razorpay account and generate your API Key ID and Key Secret from the Razorpay Dashboard.
                    </p>
                    <a
                      href="https://rzp.io/rzp/SN386Hf"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center mt-3 text-sm font-medium text-orange-700 hover:text-orange-800 underline"
                    >
                      Sign up on Razorpay and create API keys
                    </a>
                  </div>

                  {Boolean(profile.razorpay_config_updated) && !showRazorpayConfigFields ? (
                    <div className="pt-2">
                      <div className="bg-white border border-gray-200 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <p className="text-sm text-gray-700">
                          Razorpay credentials are configured. Click below to update them.
                        </p>
                        <button
                          type="button"
                          onClick={() => setShowRazorpayConfigFields(true)}
                          className="px-4 py-2 bg-orange-600 text-white text-sm font-medium rounded-lg hover:bg-orange-700 focus:ring-2 focus:ring-pink-600 focus:ring-offset-2 transition-colors"
                        >
                          Update Razorpay Configuration
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="pt-2 space-y-4">
                      <div className="bg-white border border-gray-200 rounded-lg p-4">
                        <h4 className="text-sm font-semibold text-gray-900 mb-1">
                          {Boolean(profile.razorpay_config_updated) ? 'Update Razorpay Configuration' : 'Create Razorpay Configuration'}
                        </h4>
                        <p className="text-sm text-gray-600">
                          Enter both credentials below. They are saved when both fields are provided and you move out of the input.
                        </p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                        <label htmlFor="razorpay_key_id" className="block text-sm font-medium text-gray-700 mb-2">
                          Razorpay Key ID
                        </label>
                        <input
                          id="razorpay_key_id"
                          type="text"
                          value={profile.razorpay_key_id || ''}
                          onChange={(e) => setProfile(prev => ({ ...prev, razorpay_key_id: e.target.value }))}
                          onBlur={(e) => handlePaymentFieldBlur('razorpay_key_id', e.target.value)}
                          disabled={updatingToggle === 'razorpay_key_id'}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-600 focus:border-transparent"
                          placeholder="Enter Razorpay Key ID"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Example format: starts with <span className="font-medium">rzp_</span>. Use Test key for testing and Live key for production.
                        </p>
                        </div>
                        <div>
                        <label htmlFor="razorpay_key_secret" className="block text-sm font-medium text-gray-700 mb-2">
                          Razorpay Key Secret
                        </label>
                        <input
                          id="razorpay_key_secret"
                          type="password"
                          value={profile.razorpay_key_secret || ''}
                          onChange={(e) => setProfile(prev => ({ ...prev, razorpay_key_secret: e.target.value }))}
                          onBlur={(e) => handlePaymentFieldBlur('razorpay_key_secret', e.target.value)}
                          disabled={updatingToggle === 'razorpay_key_secret'}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-600 focus:border-transparent"
                          placeholder="Enter Razorpay Key Secret"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Keep this secret private. Do not share it with staff or customers.
                        </p>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {profile.payment_option === 'upi' && (
                <div className="pt-2">
                  <label htmlFor="upi_id" className="block text-sm font-medium text-gray-700 mb-2">
                    UPI ID
                  </label>
                  <input
                    id="upi_id"
                    type="text"
                    value={profile.upi_id || ''}
                    onChange={(e) => setProfile(prev => ({ ...prev, upi_id: e.target.value }))}
                    onBlur={(e) => handlePaymentFieldBlur('upi_id', e.target.value)}
                    disabled={updatingToggle === 'upi_id'}
                    className="w-full md:w-1/2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-600 focus:border-transparent"
                    placeholder="example@bank"
                  />
                </div>
              )}

              {profile.payment_option !== 'disabled' && (
              <div className="pt-2 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Prepayment Preference
                  </label>
                  <p className="text-sm text-gray-600 mb-3">
                    Decide whether orders must be prepaid before confirmation.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[
                      {
                        id: 'required',
                        label: 'Prepayment Required',
                        helper: 'Orders are confirmed only after payment',
                        value: true
                      },
                      {
                        id: 'optional',
                        label: 'Prepayment Optional',
                        helper: 'Customers can choose to pay now or later',
                        value: false
                      }
                    ].map((item) => (
                      <label
                        key={item.id}
                        className={`flex items-start gap-3 border rounded-lg px-4 py-3 bg-white transition-colors ${
                          profile.is_prepaid === item.value ? 'border-orange-500 ring-1 ring-orange-200' : 'border-gray-200'
                        } ${updatingToggle === 'is_prepaid' ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:border-gray-300'}`}
                      >
                        <input
                          type="radio"
                          name="is_prepaid"
                          checked={Boolean(profile.is_prepaid) === item.value}
                          onChange={() => handleToggleChange('is_prepaid', item.value)}
                          disabled={updatingToggle === 'is_prepaid'}
                          className="mt-0.5 text-orange-600 focus:ring-pink-200"
                        />
                        <span>
                          <span className="block text-sm font-medium text-gray-800">{item.label}</span>
                          <span className="block text-xs text-gray-600">{item.helper}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 pr-6">
                        <h3 className="text-base font-medium text-gray-900 mb-1">
                          Stop Auto-Sending Payment Link
                        </h3>
                        <p className="text-sm text-gray-600">
                          Prevent the system from sending payment links automatically. Staff can still share links manually when needed.
                        </p>
                      </div>
                      <label className={`relative inline-flex items-center ${updatingToggle !== 'stop_auto_payment_link' ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}>
                        <input
                          type="checkbox"
                          checked={profile.stop_auto_payment_link || false}
                          onChange={(e) => handleToggleChange('stop_auto_payment_link', e.target.checked)}
                          disabled={updatingToggle === 'stop_auto_payment_link'}
                          className="sr-only peer"
                        />
                        <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-pink-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600 ${updatingToggle === 'stop_auto_payment_link' ? 'opacity-50' : ''}`}>
                          {updatingToggle === 'stop_auto_payment_link' && (
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            </div>
                          )}
                        </div>
                      </label>
                    </div>
                  </div>
              </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Theme Selection */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="space-y-6">
          <h2 className="text-xl font-semibold text-gray-900 border-b pb-2">Theme Customization</h2>
          
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Current Theme: {getCurrentTheme().name}
              </h3>
              <p className="text-gray-600 mb-6">
                Choose a predefined theme or customize your own color scheme to match your brand.
              </p>
            </div>

            {/* Predefined Themes */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">Predefined Themes</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {predefinedThemes.map((theme) => {
                  const isSelected = profile.theme?.id === theme.id;
                  return (
                    <div
                      key={theme.id}
                      onClick={() => !updatingToggle && handleThemeSelect(theme)}
                      className={`relative border-2 rounded-lg p-4 cursor-pointer transition-all ${
                        isSelected
                          ? 'border-orange-600 ring-2 ring-orange-200'
                          : 'border-gray-200 hover:border-gray-300'
                      } ${updatingToggle ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {isSelected && (
                        <div className="absolute top-2 right-2 bg-orange-600 rounded-full p-1">
                          <CheckIcon className="h-4 w-4 text-white" />
                        </div>
                      )}
                      <div className="mb-3">
                        <h5 className="font-medium text-gray-900">{theme.name}</h5>
                      </div>
                      <div className="flex gap-2">
                        <div
                          className="flex-1 h-12 rounded"
                          style={{ backgroundColor: theme.primary }}
                          title="Primary"
                        />
                        <div
                          className="flex-1 h-12 rounded"
                          style={{ backgroundColor: theme.secondary }}
                          title="Secondary"
                        />
                        <div
                          className="flex-1 h-12 rounded"
                          style={{ backgroundColor: theme.accent }}
                          title="Accent"
                        />
                      </div>
                      {updatingToggle === 'theme' && isSelected && (
                        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 rounded-lg">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-600"></div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Customize Button */}
            <div className="pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => {
                  // Initialize custom theme with current selected theme values
                  const currentTheme = getCurrentTheme();
                  setCustomTheme({
                    ...currentTheme,
                    id: 'custom',
                    name: 'Custom Theme'
                  });
                  setShowThemeCustomizer(true);
                }}
                className="px-6 py-3 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors flex items-center gap-2"
              >
                <PencilIcon className="h-5 w-5" />
                Customize Theme
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Pricing Plans Modal */}
      <PricingPlansModal
        isOpen={showPricingModal}
        onClose={() => setShowPricingModal(false)}
        showOnlyPro={profile.plan === 'basic'}
      />

      {/* Theme Customizer Modal */}
      {showThemeCustomizer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-gray-900">Customize Theme</h3>
              <button
                onClick={() => setShowThemeCustomizer(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Theme Name
                  </label>
                  <input
                    type="text"
                    value={customTheme.name}
                    onChange={(e) => setCustomTheme({ ...customTheme, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-600 focus:border-transparent"
                    placeholder="Custom Theme"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Primary Color
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={customTheme.primary}
                      onChange={(e) => setCustomTheme({ ...customTheme, primary: e.target.value })}
                      className="h-10 w-20 border border-gray-300 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={customTheme.primary}
                      onChange={(e) => setCustomTheme({ ...customTheme, primary: e.target.value })}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-600 focus:border-transparent"
                      placeholder="#2196F3"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Secondary Color
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={customTheme.secondary}
                      onChange={(e) => setCustomTheme({ ...customTheme, secondary: e.target.value })}
                      className="h-10 w-20 border border-gray-300 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={customTheme.secondary}
                      onChange={(e) => setCustomTheme({ ...customTheme, secondary: e.target.value })}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-600 focus:border-transparent"
                      placeholder="#9C27B0"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Accent Color
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={customTheme.accent}
                      onChange={(e) => setCustomTheme({ ...customTheme, accent: e.target.value })}
                      className="h-10 w-20 border border-gray-300 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={customTheme.accent}
                      onChange={(e) => setCustomTheme({ ...customTheme, accent: e.target.value })}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-600 focus:border-transparent"
                      placeholder="#03A9F4"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Background Color
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={customTheme.background}
                      onChange={(e) => setCustomTheme({ ...customTheme, background: e.target.value })}
                      className="h-10 w-20 border border-gray-300 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={customTheme.background}
                      onChange={(e) => setCustomTheme({ ...customTheme, background: e.target.value })}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-600 focus:border-transparent"
                      placeholder="#f5f7fa"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Surface Color
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={customTheme.surface}
                      onChange={(e) => setCustomTheme({ ...customTheme, surface: e.target.value })}
                      className="h-10 w-20 border border-gray-300 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={customTheme.surface}
                      onChange={(e) => setCustomTheme({ ...customTheme, surface: e.target.value })}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-600 focus:border-transparent"
                      placeholder="#ffffff"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Text Color
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={customTheme.text}
                      onChange={(e) => setCustomTheme({ ...customTheme, text: e.target.value })}
                      className="h-10 w-20 border border-gray-300 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={customTheme.text}
                      onChange={(e) => setCustomTheme({ ...customTheme, text: e.target.value })}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-600 focus:border-transparent"
                      placeholder="#212121"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Secondary Text Color
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={customTheme.textSecondary}
                      onChange={(e) => setCustomTheme({ ...customTheme, textSecondary: e.target.value })}
                      className="h-10 w-20 border border-gray-300 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={customTheme.textSecondary}
                      onChange={(e) => setCustomTheme({ ...customTheme, textSecondary: e.target.value })}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-600 focus:border-transparent"
                      placeholder="#757575"
                    />
                  </div>
                </div>
              </div>

              {/* Preview */}
              {/* <div className="border-t border-gray-200 pt-6">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Preview</h4>
                <div
                  className="rounded-lg p-6 border-2 border-gray-200"
                  style={{ backgroundColor: customTheme.background }}
                >
                  <div
                    className="rounded-lg p-4 mb-4"
                    style={{ backgroundColor: customTheme.surface }}
                  >
                    <h5
                      className="text-lg font-semibold mb-2"
                      style={{ color: customTheme.text }}
                    >
                      Theme Preview
                    </h5>
                    <p
                      className="text-sm mb-4"
                      style={{ color: customTheme.textSecondary }}
                    >
                      This is how your theme colors will appear
                    </p>
                    <div className="flex gap-2">
                      <button
                        className="px-4 py-2 rounded text-white text-sm font-medium"
                        style={{ backgroundColor: customTheme.primary }}
                      >
                        Primary
                      </button>
                      <button
                        className="px-4 py-2 rounded text-white text-sm font-medium"
                        style={{ backgroundColor: customTheme.secondary }}
                      >
                        Secondary
                      </button>
                      <button
                        className="px-4 py-2 rounded text-white text-sm font-medium"
                        style={{ backgroundColor: customTheme.accent }}
                      >
                        Accent
                      </button>
                    </div>
                  </div>
                </div>
              </div> */}

              {/* Actions */}
              <div className="flex flex-col md:flex-row md:justify-between gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleResetTheme}
                  className="px-6 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors flex items-center justify-center gap-2 w-full md:w-auto"
                >
                  <ArrowPathIcon className="h-5 w-5" />
                  Reset
                </button>
                <div className="flex flex-col md:flex-row gap-3 w-full md:w-auto">
                  <button
                    type="button"
                    onClick={() => setShowThemeCustomizer(false)}
                    className="px-6 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors w-full md:w-auto"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleCustomThemeSave}
                    disabled={updatingToggle === 'theme'}
                    className="px-6 py-2 bg-orange-600 text-white font-medium rounded-lg hover:bg-orange-700 focus:ring-2 focus:ring-pink-600 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 w-full md:w-auto"
                  >
                    {updatingToggle === 'theme' ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Saving...
                      </>
                    ) : (
                      'Save Theme'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Settings;
